import { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';

const EXPERIENCE_VIEW_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All Experiences' },
  { value: 'past', label: 'Past Events' },
];

export default function AppPreferences() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    defaultExperienceView: 'active',
    theme: 'light',
  });

  useEffect(() => {
    if (user) {
      const loadPreferences = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.preferences?.defaults) {
              setPreferences({
                defaultExperienceView: data.preferences.defaults.projectView || 'active',
                theme: data.preferences.theme || 'light',
              });
            }
          }
        } catch (error) {
          console.error('Error loading preferences:', error);
        }
      };
      loadPreferences();
    }
  }, [user]);

  const handleSave = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const currentData = userDoc.exists() ? userDoc.data() : {};

      await updateDoc(userDocRef, {
        preferences: {
          ...currentData.preferences,
          defaults: {
            projectView: preferences.defaultExperienceView,
          },
          theme: preferences.theme,
        },
        updatedAt: new Date(),
      });

      toast.success('App preferences updated!');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update app preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-card shadow-card border border-border p-6 sm:p-8">
      <h2 className="text-xl sm:text-2xl font-semibold text-text-primary mb-6">App Preferences</h2>

      {/* Default Experience View */}
      <div className="mb-6">
        <label htmlFor="experienceView" className="block text-sm font-medium text-text-primary mb-2">
          Default Experience View
        </label>
        <select
          id="experienceView"
          value={preferences.defaultExperienceView}
          onChange={(e) => setPreferences({ ...preferences, defaultExperienceView: e.target.value })}
          className="w-full px-4 py-2.5 text-base border border-border rounded-lg focus:outline-none focus:border-primary transition-all bg-white"
        >
          {EXPERIENCE_VIEW_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-text-secondary mt-1">
          Choose which tab to show by default on the Dashboard
        </p>
      </div>

      {/* Theme (Stub) */}
      <div className="mb-6">
        <label htmlFor="theme" className="block text-sm font-medium text-text-primary mb-2">
          Theme
        </label>
        <select
          id="theme"
          value={preferences.theme}
          onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
          disabled
          className="w-full px-4 py-2.5 text-base border border-border rounded-lg bg-surface text-text-secondary cursor-not-allowed"
        >
          <option value="light">Light</option>
          <option value="dark" disabled>Dark (Coming Soon)</option>
        </select>
        <p className="text-xs text-text-secondary mt-1">
          Dark mode coming soon
        </p>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={loading}
        className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faSave} />
        <span>{loading ? 'Saving...' : 'Save Changes'}</span>
      </button>
    </div>
  );
}

